import { useCallback } from 'react';
import { useTaggableTableRowActions } from '@/features/shared/hooks/useTaggableTableRowActions';
import { useDeleteTest, useUpdateTest } from '@/features/tests/hooks/tests';
import type { TagColor } from '@/shared/data/tags';
import type { TestListItemDto } from '@flowqa/shared';

type DeleteTarget = {
  id: string;
  name: string;
  projectId: string;
};

type UseTestsTableRowActionsParams = { selectedProjectId: string | null };

type UseTestsTableRowActionsResult = {
  deleteTarget: DeleteTarget | null;
  deleteLoading: boolean;
  deleteRowPendingId: string | null;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: TestListItemDto) => void;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onUpdateRowTagsColor: (input: {
    row: TestListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
};

export const useTestsTableRowActions = ({
  selectedProjectId,
}: UseTestsTableRowActionsParams): UseTestsTableRowActionsResult => {
  const deleteTestMutation = useDeleteTest();
  const updateTestMutation = useUpdateTest();
  const updateRowTags = useCallback(
    async ({ row, tags }: { row: TestListItemDto; tags: string[] }) => {
      await updateTestMutation.mutateAsync({ id: row.id, tags });
    },
    [updateTestMutation],
  );

  return useTaggableTableRowActions({
    deleteMutation: deleteTestMutation,
    entity: 'test',
    selectedProjectId,
    updateRowTags,
    updateRowTagsPending: updateTestMutation.isPending,
  });
};
